import { VisitDaySchedule } from '../VisitGradeInput';

/**
 * Builds the person_role a "liberação" is made of, matching what the morador app sends so a
 * liberação created at the portaria is indistinguishable from one the resident created.
 *
 * There is no liberacoes table: the mode, the unit, the window and the intercom rule all live in
 * role_metadata (see utils/liberacoes for the read side).
 */

export type LiberacaoMode = 'single' | 'periodo' | 'irrestrito';
export type LiberacaoCategory = 'visitor' | 'service_provider';

/** The app caps a liberação at six months; the backend rejects anything longer. */
export const LIBERACAO_MAX_DAYS = 180;

export interface LiberacaoDraft {
  mode: LiberacaoMode;
  category: LiberacaoCategory;
  requiresIntercom: boolean;
  /** 'YYYY-MM-DD' */
  validFrom?: string;
  /** 'YYYY-MM-DD' */
  validUntil?: string;
  /**
   * 'HH:MM' — the hour a VISITA ÚNICA was arranged for ("o pintor vem quinta às 14h").
   *
   * Informational, like the rest of a liberação: it moves valid_from to that moment and is stored
   * as expected_arrival_at so the portaria can see what was combined. It does NOT shut the gate at
   * 14:31 — valid_until stays at the end of the day.
   */
  visitTime?: string;
  visitSchedule?: VisitDaySchedule[];
  unitId: string;
  unitDisplay?: string;
  blockId?: string;
  blockName?: string;
  unitNumber?: string;
  authorizedByResidentId?: string;
  authorizedByName?: string;
  /** Person id of whoever is creating it, for the "por <name>" line. */
  addedBy?: string;
  purpose?: string;
}

const endOfDay = (day: string): string => {
  const d = new Date(`${day}T00:00:00`);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

const startOfDay = (day: string): string => new Date(`${day}T00:00:00`).toISOString();

const daysBetween = (from: Date, to: Date): number =>
  Math.ceil((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000));

/**
 * Returns the first problem with the draft in Portuguese, or null when it is good to send.
 * Deliberately the same rules the Android sheet enforces, so the two surfaces accept the same
 * things — a liberação refused here but accepted there would be impossible to explain.
 */
export function validateLiberacaoDraft(draft: LiberacaoDraft, now: Date = new Date()): string | null {
  if (!draft.unitId) return 'Selecione a unidade';

  if (draft.mode === 'single') {
    // An hour with no day is not a time, it is a question.
    if (draft.visitTime && !draft.validUntil) return 'Informe o dia da visita';
    if (draft.validUntil) {
      const day = new Date(`${draft.validUntil}T23:59:59`);
      if (isNaN(day.getTime())) return 'Data inválida';
      if (day.getTime() <= now.getTime()) return 'O dia da visita deve ser uma data futura';
    }
  }

  if (draft.mode === 'periodo') {
    if (!draft.validUntil) return 'Informe a data final do período';
    const until = new Date(`${draft.validUntil}T23:59:59`);
    if (isNaN(until.getTime())) return 'Data final inválida';
    if (until.getTime() <= now.getTime()) return 'A data final deve ser futura';

    const from = draft.validFrom ? new Date(`${draft.validFrom}T00:00:00`) : now;
    if (draft.validFrom && isNaN(from.getTime())) return 'Data inicial inválida';
    if (draft.validFrom && from.getTime() > until.getTime()) return 'A data inicial deve ser antes da final';
    if (daysBetween(from, until) > LIBERACAO_MAX_DAYS) {
      return `O período não pode exceder 6 meses (${LIBERACAO_MAX_DAYS} dias)`;
    }

    for (const day of draft.visitSchedule || []) {
      for (const w of day.windows || []) {
        if (!w.start || !w.end) return 'Preencha os horários da grade';
        if (w.end <= w.start) return 'O horário final deve ser depois do inicial';
      }
    }
  }

  if (draft.mode === 'irrestrito' && draft.validUntil) {
    const until = new Date(`${draft.validUntil}T23:59:59`);
    if (isNaN(until.getTime())) return 'Data final inválida';
    if (until.getTime() <= now.getTime()) return 'A data final deve ser futura';
    if (daysBetween(now, until) > LIBERACAO_MAX_DAYS) {
      return `O período não pode exceder 6 meses (${LIBERACAO_MAX_DAYS} dias)`;
    }
  }

  return null;
}

export interface LiberacaoRolePayload {
  role_type: 'visitor';
  valid_from: string;
  valid_until?: string;
  role_metadata: Record<string, any>;
  is_authorized: boolean;
}

export function buildLiberacaoRolePayload(draft: LiberacaoDraft, now: Date = new Date()): LiberacaoRolePayload {
  const metadata: Record<string, any> = {
    unit_id: draft.unitId,
    unit_ids: [draft.unitId],
    // Liberações are pre-arranged by definition; the state machine moves them on at the gate.
    visitor_state: 'pre_registered',
    category: draft.category,
    requires_intercom: draft.requiresIntercom,
    visit_count: 0,
  };

  if (draft.unitDisplay) {
    metadata.unit_display = draft.unitDisplay;
    metadata.unit = draft.unitDisplay;
  }
  if (draft.unitNumber) metadata.unit = draft.unitNumber;
  if (draft.blockName) metadata.block = draft.blockName;
  if (draft.blockId) metadata.block_id = draft.blockId;
  if (draft.authorizedByResidentId) metadata.authorized_by_resident_id = draft.authorizedByResidentId;
  if (draft.authorizedByName) metadata.authorized_by_name = draft.authorizedByName;
  if (draft.addedBy) metadata.added_by = draft.addedBy;
  if (draft.purpose) metadata.purpose = draft.purpose;

  let validUntil: string | undefined;

  // The hour a visita única was arranged for: valid_from moves to that moment (so the card can say
  // "dia 08/10 às 14:30" and the clearance reads as "not started" before it) while valid_until
  // stays at the end of the day — the hour is what was combined, not a gate that shuts at 14:31.
  let validFromOverride: string | undefined;

  if (draft.mode === 'single') {
    metadata.access_type = 'single_visit';
    metadata.max_visits = 1;
    if (draft.validUntil) validUntil = endOfDay(draft.validUntil);
    if (draft.validUntil && draft.visitTime) {
      const at = new Date(`${draft.validUntil}T${draft.visitTime}:00`);
      if (!isNaN(at.getTime())) {
        validFromOverride = at.toISOString();
        metadata.expected_arrival_at = validFromOverride;
      }
    } else if (draft.validUntil) {
      validFromOverride = startOfDay(draft.validUntil);
    }
  } else if (draft.mode === 'periodo') {
    metadata.access_type = 'temporary';
    metadata.custom_period_access = true;
    metadata.max_visits = 999;
    if (draft.visitSchedule && draft.visitSchedule.length > 0) {
      metadata.visit_schedule = draft.visitSchedule;
    }
    validUntil = draft.validUntil ? endOfDay(draft.validUntil) : undefined;
  } else {
    metadata.access_type = 'temporary';
    metadata.always_enabled = true;
    metadata.max_visits = 999;
    // The app defaults an open-ended irrestrito to the six-month cap rather than storing "forever".
    validUntil = draft.validUntil
      ? endOfDay(draft.validUntil)
      : new Date(now.getTime() + LIBERACAO_MAX_DAYS * 24 * 60 * 60 * 1000).toISOString();
  }

  return {
    // Always 'visitor', even for a prestador — this is what the morador app writes, and
    // GetVisitorsForSelf filters on it, so a prestador liberação created here still shows up in the
    // resident's Liberações screen. `category` is what tells the two apart.
    role_type: 'visitor',
    valid_from: draft.validFrom ? startOfDay(draft.validFrom) : (validFromOverride ?? now.toISOString()),
    valid_until: validUntil,
    role_metadata: metadata,
    // A liberação is a MARK on the person for this unit, not a visit in progress — exactly how the
    // resident app writes it (person_handler.go:4302, `IsAuthorized: false`). Authorising it here
    // would run initializeVisitorStateMachine immediately, which opens a 15-MINUTE entry window:
    // a liberação for next week would expire before the operator finished reading it. The visit is
    // authorised when the person actually turns up.
    is_authorized: false,
  };
}
